import type { Form1099SummaryItem, Form1099SummaryReport } from '@/services/accounting-service';
import type { TreeReport, TreeReportDefinition, TreeRow, TreeSection } from '@/types/report-definition';

export type Form1099SummaryReportDefinition = TreeReportDefinition<Form1099SummaryReport>;

const taxIdLabel = (item: Form1099SummaryItem): string =>
	item.hasTaxId ? (item.taxId ?? '') : '(no tax ID on file)';

const vendorDisplayName = (item: Form1099SummaryItem): string =>
	item.printAs ?? item.name;

/**
 * QuickBooks-style 1099 Summary — one row per vendor showing payment totals
 * for the year. The app has no 1099-box-to-GL mapping yet, so all activity
 * lands in the "Uncategorized" bucket (matches how QB renders the report
 * when boxes aren't configured).
 *
 * Each vendor appears as TWO TreeRows: the vendor name carries the amounts,
 * and an indented heading row underneath shows the tax-ID (or "no tax ID on
 * file"). Heading rows render label-only — see TreeRowView's `isHeading`
 * branch.
 */
export const form1099SummaryReport: Form1099SummaryReportDefinition = {
	id: 'form-1099-summary',
	title: '1099 Summary',
	subtitle: 'Payments to vendors over the calendar year.',
	flavor: 'tree',
	printUrl: (associationId, params) => {
		const sp = new URLSearchParams();
		if (params.year) sp.set('year', String(params.year));
		const qs = sp.toString();
		return `/print/form-1099/${associationId}${qs ? `?${qs}` : ''}`;
	},
	buildTree: (data: Form1099SummaryReport): TreeReport => {
		const rows: TreeRow[] = [];
		for (const item of data.items) {
			rows.push({
				id: item.vendorId,
				label: vendorDisplayName(item),
				amount: item.uncategorizedCents,
				ytdAmount: item.totalCents,
			});
			rows.push({
				id: `${item.vendorId}-taxid`,
				label: taxIdLabel(item),
				amount: 0,
				indent: 1,
				heading: true,
			});
		}

		const section: TreeSection = {
			id: 'vendors',
			title: '',
			rows,
			total: {
				label: 'TOTAL',
				amount: data.totals.uncategorizedCents,
				ytdAmount: data.totals.totalCents,
			},
			emptyMessage: 'No vendor payments in this year.',
		};

		return {
			columns: [{ sections: [section] }],
			amountColumnLabels: { primary: 'Uncategorized', secondary: 'TOTAL' },
		};
	},
	buildExport: (data: Form1099SummaryReport) => ({
		columns: [
			{ header: 'Vendor', accessor: (r) => r.account, width: 280 },
			{ header: 'Tax ID', accessor: (r) => r.code, width: 120 },
			{ header: 'Total', accessor: (r) => r.amount, format: 'money', width: 120 },
		],
		rows: data.items.map((item) => ({
			section: '',
			code: item.hasTaxId ? (item.taxId ?? '') : '(no tax ID)',
			account: vendorDisplayName(item),
			amount: item.totalCents,
		})),
	}),
};
